/**
 * Phone Number Formatting Utility
 * Formats phone numbers to American format: (XXX) XXX-XXXX
 */

/**
 * Format a phone number string to (XXX) XXX-XXXX format
 * @param value - The input value (can contain digits, spaces, dashes, parentheses)
 * @returns Formatted phone number string
 */
export function formatPhoneNumber(value: string): string {
  if (!value) {
    return '';
  }

  // Remove all non-numeric characters
  const numbers = value.replace(/\D/g, '');

  // Limit to 10 digits
  const limitedNumbers = numbers.slice(0, 10);

  // Format based on length
  if (limitedNumbers.length === 0) {
    return '';
  } else if (limitedNumbers.length <= 3) {
    return `(${limitedNumbers}`;
  } else if (limitedNumbers.length <= 6) {
    return `(${limitedNumbers.slice(0, 3)}) ${limitedNumbers.slice(3)}`;
  } else {
    return `(${limitedNumbers.slice(0, 3)}) ${limitedNumbers.slice(3, 6)}-${limitedNumbers.slice(6)}`;
  }
}

/**
 * Get the cursor position after formatting
 * @param oldValue - Previous value
 * @param newValue - New formatted value
 * @param oldCursorPosition - Previous cursor position
 * @returns New cursor position
 */
export function getPhoneCursorPosition(
  oldValue: string,
  newValue: string,
  oldCursorPosition: number
): number {
  // Count digits before cursor in old value
  const beforeCursor = oldValue.substring(0, oldCursorPosition);
  const digitsBeforeCursor = beforeCursor.replace(/\D/g, '').length;

  // Find position in new value where we have the same number of digits
  let digitCount = 0;
  for (let i = 0; i < newValue.length; i++) {
    if (/\d/.test(newValue[i])) {
      digitCount++;
      if (digitCount === digitsBeforeCursor) {
        // Position cursor after this digit
        return i + 1;
      }
    }
  }

  // If we've counted all digits, position at end
  return newValue.length;
}

/**
 * Check if a phone number has exactly 10 digits
 * @param value - The phone number string
 * @returns true if exactly 10 digits, false otherwise
 */
export function isValidPhoneNumber(value: string): boolean {
  if (!value) {
    return false;
  }
  const numbers = value.replace(/\D/g, '');
  return numbers.length === 10;
}
